import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Inbox, Check, X, Loader2, FileText, ArrowLeft, ScanLine, Mail, AlertTriangle, Eye, History, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  "Pending Review": "bg-amber-50 text-amber-700 border-amber-200",
  Approved: "bg-green-50 text-green-700 border-green-200",
  Rejected: "bg-red-50 text-red-600 border-red-200",
};

export default function EmailImportsPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "imports";

  if (id) return <ImportDetail importId={id} />;

  return (
    <div data-testid="email-imports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Email Imports</h1>
          <p className="text-sm text-gray-500 mt-1">Auto-imported invoices, quotes, and sync activity</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          data-testid="tab-imports"
          onClick={() => setSearchParams({})}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === "imports" ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
        >
          <Inbox className="w-4 h-4 inline mr-1.5 -mt-0.5" />Pending Imports
        </button>
        <button
          data-testid="tab-sync-log"
          onClick={() => setSearchParams({ tab: "sync-log" })}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === "sync-log" ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
        >
          <History className="w-4 h-4 inline mr-1.5 -mt-0.5" />Sync Activity Log
        </button>
      </div>

      {tab === "sync-log" ? <SyncLog /> : <ImportList />}
    </div>
  );
}

function ImportList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Pending Review");
  const navigate = useNavigate();

  const fetch = () => {
    setLoading(true);
    api.get("/email-imports", { params: { status: filter || undefined, page, limit: 20 } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, [page, filter]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{total} import{total !== 1 ? "s" : ""}</p>
      </div>

      <div className="flex gap-2 mb-4">
        {["Pending Review", "Approved", "Rejected", ""].map((s) => (
          <Button key={s || "all"} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => { setFilter(s); setPage(1); }}
            className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>
            {s || "All"}
          </Button>
        ))}
      </div>

      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border border-gray-200 rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No email imports found</p></CardContent></Card>
      ) : (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Subject</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">From</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">OCR Total</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Confidence</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              </tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.import_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/email-imports/${item.import_id}`)}>
                    <td className="p-3"><Badge className={`text-xs rounded-full ${item.import_type === "invoice" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{item.import_type}</Badge></td>
                    <td className="p-3 max-w-[250px] truncate">{item.email_subject}</td>
                    <td className="p-3 text-xs text-gray-500">{item.supplier_name || item.email_from}</td>
                    <td className="p-3 font-mono text-sm">{item.ocr_total ? `R${Number(item.ocr_total).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}` : "—"}</td>
                    <td className="p-3"><Badge className={`text-[10px] rounded-full ${item.ocr_confidence >= 75 ? "bg-green-50 text-green-700" : item.ocr_confidence >= 50 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>{item.ocr_confidence || 0}%</Badge></td>
                    <td className="p-3"><Badge className={`text-xs rounded-full border ${statusColors[item.status] || ""}`}>{item.status}</Badge></td>
                    <td className="p-3 text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ImportDetail({ importId }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/email-imports/${importId}`).then((r) => setItem(r.data)).catch(() => toast.error("Not found")).finally(() => setLoading(false));
  }, [importId]);

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!item) return <p>Not found</p>;

  return (
    <div data-testid="email-import-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate("/email-imports")} className="mb-4 text-gray-500"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-xl flex items-center gap-2">
            <Badge className={`text-xs rounded-full ${item.import_type === "invoice" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{item.import_type}</Badge>
            {item.email_subject}
            <Badge className={`text-xs rounded-full border ${statusColors[item.status] || ""}`}>{item.status}</Badge>
          </h1>
          <p className="text-sm text-gray-500 mt-1">From: {item.email_from_name || item.email_from} — {new Date(item.email_date || item.created_at).toLocaleString()}</p>
        </div>
        {item.status === "Pending Review" && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setApproveOpen(true)} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Check className="w-4 h-4 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><X className="w-4 h-4 mr-1" /> Reject</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold flex items-center gap-2"><ScanLine className="w-4 h-4" /> OCR Extracted Data</CardTitle></CardHeader>
          <CardContent className="px-6 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500 text-xs uppercase block">Vendor</span><span className="font-medium">{item.ocr_vendor || "—"}</span></div>
              <div><span className="text-gray-500 text-xs uppercase block">Invoice #</span><span className="font-medium">{item.ocr_invoice_number || "—"}</span></div>
              <div><span className="text-gray-500 text-xs uppercase block">Date</span><span className="font-medium">{item.ocr_date || "—"}</span></div>
              <div><span className="text-gray-500 text-xs uppercase block">Total</span><span className="font-bold text-lg text-[#E63946]">{item.ocr_total ? `R${Number(item.ocr_total).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}` : "—"}</span></div>
              <div><span className="text-gray-500 text-xs uppercase block">VAT</span><span className="font-medium">{item.ocr_vat ? `R${Number(item.ocr_vat).toFixed(2)}` : "—"}</span></div>
              <div><span className="text-gray-500 text-xs uppercase block">Confidence</span>
                <Badge className={`text-xs rounded-full ${item.ocr_confidence >= 75 ? "bg-green-50 text-green-700" : item.ocr_confidence >= 50 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>{item.ocr_confidence || 0}%</Badge></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardHeader className="pb-2 px-6 pt-5"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4" /> Email Details</CardTitle></CardHeader>
          <CardContent className="px-6 pb-4 space-y-2 text-sm">
            <div><span className="text-gray-500">From:</span> {item.email_from_name} &lt;{item.email_from}&gt;</div>
            <div><span className="text-gray-500">Subject:</span> {item.email_subject}</div>
            <div><span className="text-gray-500">Supplier:</span> {item.supplier_name || "Unknown"}</div>
            {item.files?.length > 0 && (
              <div><span className="text-gray-500">Attachments:</span>
                {item.files.map((f, i) => <Badge key={i} className="ml-1 text-[10px] bg-gray-100 text-gray-600 rounded-full">{f.filename}</Badge>)}
              </div>
            )}
            {item.reviewed_by && <div className="mt-3 pt-2 border-t"><span className="text-gray-500">Reviewed by:</span> {item.reviewed_by} — {new Date(item.reviewed_at).toLocaleString()}</div>}
            {item.reject_reason && <div className="text-red-600 text-xs"><AlertTriangle className="w-3 h-3 inline mr-1" />Reason: {item.reject_reason}</div>}
          </CardContent>
        </Card>
      </div>

      <ApproveDialog open={approveOpen} item={item} onClose={() => setApproveOpen(false)} onApproved={(d) => { setItem({ ...item, status: "Approved" }); setApproveOpen(false); toast.success("Approved"); }} />
      <RejectDialog open={rejectOpen} item={item} onClose={() => setRejectOpen(false)} onRejected={() => { setItem({ ...item, status: "Rejected" }); setRejectOpen(false); toast.success("Rejected"); }} />
    </div>
  );
}

function ApproveDialog({ open, item, onClose, onApproved }) {
  const [form, setForm] = useState({
    vendor: item?.ocr_vendor || item?.supplier_name || "",
    amount: item?.ocr_total ? String(item.ocr_total) : "",
    expense_date: item?.ocr_date || "",
    description: `Auto-imported: ${item?.email_subject || ""}`,
    category_name: "Other",
  });
  const [saving, setSaving] = useState(false);

  const handleApprove = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/email-imports/${item.import_id}/approve`, { ...form, amount: parseFloat(form.amount) || 0 });
      onApproved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent className="max-w-md rounded-sm">
      <DialogHeader><DialogTitle>Approve & Create Expense</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Vendor</label>
          <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="rounded-sm" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Amount (R)</label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm" /></div>
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Date</label>
            <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="rounded-sm" /></div>
        </div>
        <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Description</label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
        <Button onClick={handleApprove} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Approve & Create Expense
        </Button>
      </DialogFooter>
    </DialogContent></Dialog>
  );
}

function RejectDialog({ open, item, onClose, onRejected }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const handleReject = async () => {
    setSaving(true);
    try {
      await api.post(`/email-imports/${item.import_id}/reject`, { reason });
      onRejected();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent className="max-w-sm rounded-sm">
      <DialogHeader><DialogTitle>Reject Import</DialogTitle></DialogHeader>
      <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Reason (optional)</label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why reject this import?" className="rounded-sm" /></div>
      <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
        <Button onClick={handleReject} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />} Reject
        </Button>
      </DialogFooter>
    </DialogContent></Dialog>
  );
}


function SyncLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pages = Math.max(1, Math.ceil(total / 25));

  useEffect(() => {
    setLoading(true);
    api.get(`/email-sync/log?page=${page}&limit=25`)
      .then((r) => { setLogs(r.data.logs || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("Failed to load sync log"))
      .finally(() => setLoading(false));
  }, [page]);

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) + " " + dt.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return <div className="animate-pulse h-40 bg-gray-100 rounded-sm" />;

  if (logs.length === 0) return (
    <Card className="border border-gray-200 rounded-sm">
      <CardContent className="p-12 text-center text-gray-400">
        <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No sync activity yet</p>
        <p className="text-xs mt-1">Logs will appear here after the first email scan runs</p>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">{total} log entr{total !== 1 ? "ies" : "y"}</p>
      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <div className="overflow-x-auto">
          <table data-testid="sync-log-table" className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/50">
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Trigger</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Mailbox</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Invoices</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Quotes</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Comms</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Error</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Time</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.log_id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="p-3">
                    {log.success ? (
                      <Badge data-testid={`log-status-${log.log_id}`} className="bg-green-50 text-green-700 border border-green-200 rounded-full text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Success</Badge>
                    ) : (
                      <Badge data-testid={`log-status-${log.log_id}`} className="bg-red-50 text-red-600 border border-red-200 rounded-full text-xs"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge className={`rounded-full text-xs ${log.trigger === "manual" ? "bg-blue-50 text-blue-600 border border-blue-200" : "bg-gray-100 text-gray-600"}`}>
                      {log.trigger === "manual" ? "Manual" : "Scheduled"}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-gray-600">{log.sync_email || "—"}</td>
                  <td className="p-3 text-center font-mono text-sm">{log.stats?.invoices || 0}</td>
                  <td className="p-3 text-center font-mono text-sm">{log.stats?.quotes || 0}</td>
                  <td className="p-3 text-center font-mono text-sm">{log.stats?.communications || 0}</td>
                  <td className="p-3 text-xs text-red-500 max-w-[200px] truncate">{log.error || "—"}</td>
                  <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-sm h-8">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-sm h-8">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
